(() => {
  const CART_READ_PATH = /\/cart\.js$/;
  const CART_CHANGE_PATH = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const REUSE_TIME_MS = 1000;

  const originalFetch = window.fetch;
  const originalXhrOpen = XMLHttpRequest.prototype.open;

  let cartVersion = 0;
  let cartChangesInProgress = 0;
  let sharedCartRead = null;
  let releaseWaitingReads = null;
  let cartChangesSettled = Promise.resolve();

  const getRequestUrl = (input) => {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.href;
    return input?.url ?? "";
  };

  const getRequestMethod = (input, init) =>
    String(init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();

  const getSameOriginPath = (url) => {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      return parsedUrl.origin === window.location.origin ? parsedUrl.pathname : null;
    } catch (error) {
      return null;
    }
  };

  const isCartChange = (url) => CART_CHANGE_PATH.test(getSameOriginPath(url) ?? "");

  const isCartRead = (url, method) => method === "GET" && CART_READ_PATH.test(getSameOriginPath(url) ?? "");

  const markCartChanged = () => {
    cartVersion += 1;
    sharedCartRead = null;
  };

  const startCartChange = () => {
    markCartChanged();
    if (cartChangesInProgress === 0) {
      cartChangesSettled = new Promise((resolve) => {
        releaseWaitingReads = resolve;
      });
    }
    cartChangesInProgress += 1;
  };

  const finishCartChange = () => {
    markCartChanged();
    cartChangesInProgress = Math.max(cartChangesInProgress - 1, 0);
    if (cartChangesInProgress === 0) releaseWaitingReads?.();
  };

  const isFreshRead = (cartRead) =>
    !!cartRead &&
    cartRead.version === cartVersion &&
    (cartRead.finishedAt === null || Date.now() - cartRead.finishedAt < REUSE_TIME_MS);

  const startSharedRead = (input, init) => {
    const cartRead = { version: cartVersion, finishedAt: null, response: null };
    cartRead.response = originalFetch.call(window, input, init).then(
      (response) => {
        if (sharedCartRead !== cartRead) return response;
        if (response.ok) cartRead.finishedAt = Date.now();
        else sharedCartRead = null;
        return response;
      },
      (error) => {
        if (sharedCartRead === cartRead) sharedCartRead = null;
        throw error;
      }
    );
    sharedCartRead = cartRead;
    return cartRead;
  };

  const readCartTogether = async (input, init) => {
    while (cartChangesInProgress > 0) await cartChangesSettled;
    const cartRead = isFreshRead(sharedCartRead) ? sharedCartRead : startSharedRead(input, init);
    const response = await cartRead.response;
    return response.clone();
  };

  window.fetch = function (input, init) {
    const url = getRequestUrl(input);
    const method = getRequestMethod(input, init);

    if (isCartChange(url)) {
      startCartChange();
      return originalFetch.call(window, input, init).finally(finishCartChange);
    }

    if (!isCartRead(url, method)) {
      return originalFetch.call(window, input, init);
    }

    return readCartTogether(input, init);
  };

  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartChange(String(url))) {
      startCartChange();
      this.addEventListener("loadend", finishCartChange, { once: true });
    }
    return originalXhrOpen.apply(this, arguments);
  };
})();
